import {
  getLocalTrack,
  MEDIA_TYPE,
  useTracksStore,
} from '@cometchat/calls-sdk-core';
import { create } from 'zustand';
import { combine, persist, subscribeWithSelector } from 'zustand/middleware';
import { createVirtualBackgroundEffect } from './index';
import type { IBackgroundEffectOptions } from './JitsiStreamBackgroundEffect';

export type VirtualBackgroundType =
  | 'half-blur'
  | 'blur'
  | 'image'
  | 'none'
  | 'add';

export type BackgroundOption = {
  id: string;
  type: VirtualBackgroundType;
  src?: string;
  label?: string;
  selected: boolean;
};

type VirtualBackgroundState = {
  previewTrack?: any;
  previewVirtualBackground: IBackgroundEffectOptions['virtualBackground'];
  virtualBackground: IBackgroundEffectOptions['virtualBackground'];
  backgrounds: BackgroundOption[];
  selectedBackgroundId: string;
};

type Actions = {
  reset: () => void;
  setPreviewVirtualBackground: <T extends VirtualBackgroundType>(
    type: T,
    ...args: T extends 'image' ? [virtualSource: string] : []
  ) => void;
  setVirtualBackground: <T extends VirtualBackgroundType>(
    type: T,
    ...args: T extends 'image' ? [virtualSource: string] : []
  ) => void;
  promotePreviewVirtualBackground: () => void;
  selectBackground: (id: string) => void;
};

const initialState: VirtualBackgroundState = {
  previewVirtualBackground: {
    backgroundType: 'none',
  },
  virtualBackground: {
    backgroundType: 'none',
  },
  selectedBackgroundId: 'none',
  backgrounds: [
    { id: 'none', type: 'none', label: 'None', selected: false },
    { id: 'half-blur', type: 'blur', label: 'Slightly Blur', selected: false },
    { id: 'blur', type: 'blur', label: 'Blur', selected: false },
    {
      id: 'add-background',
      type: 'add',
      label: 'Add Background',
      selected: false,
    },
    {
      id: 'background-1',
      type: 'image',
      src: 'https://cdn.cometchat.io/calls/v5/virtual-backgrounds/background-1.jpg',
      label: 'Background 1',
      selected: false,
    },
    {
      id: 'background-2',
      type: 'image',
      src: 'https://cdn.cometchat.io/calls/v5/virtual-backgrounds/background-2.jpg',
      label: 'Background 2',
      selected: false,
    },
    {
      id: 'background-3',
      type: 'image',
      src: 'https://cdn.cometchat.io/calls/v5/virtual-backgrounds/background-3.jpg',
      label: 'Background 3',
      selected: false,
    },
    {
      id: 'background-4',
      type: 'image',
      src: 'https://cdn.cometchat.io/calls/v5/virtual-backgrounds/background-4.jpg',
      label: 'Background 4',
      selected: false,
    },
    {
      id: 'background-5',
      type: 'image',
      src: 'https://cdn.cometchat.io/calls/v5/virtual-backgrounds/background-5.jpg',
      label: 'Background 5',
      selected: false,
    },
    {
      id: 'background-6',
      type: 'image',
      src: 'https://cdn.cometchat.io/calls/v5/virtual-backgrounds/background-6.jpg',
      label: 'Background 6',
      selected: false,
    },
  ],
};

export const useVirtualBackgroundStore = create<
  VirtualBackgroundState & Actions
>()(
  subscribeWithSelector(
    persist(
      combine(initialState, set => ({
        setPreviewVirtualBackground: <T extends VirtualBackgroundType>(
          type: T,
          ...args: T extends 'image' ? [virtualSource: string] : []
        ) => {
          const virtualSource = args[0];

          useVirtualBackgroundStore.setState(() => {
            if (type === 'none') {
              return {
                previewVirtualBackground: {
                  virtualSource: undefined,
                  backgroundType: 'none',
                  blurValue: undefined,
                },
              };
            }
            if (type === 'half-blur') {
              return {
                previewVirtualBackground: {
                  virtualSource: undefined,
                  backgroundType: 'blur',
                  blurValue: 8,
                },
              };
            }
            if (type === 'blur') {
              return {
                previewVirtualBackground: {
                  virtualSource: undefined,
                  backgroundType: 'blur',
                  blurValue: 25,
                },
              };
            }
            return {
              previewVirtualBackground: {
                virtualSource: virtualSource,
                backgroundType: 'image',
                blurValue: undefined,
              },
            };
          });
        },
        setVirtualBackground: <T extends VirtualBackgroundType>(
          type: T,
          ...args: T extends 'image' ? [virtualSource: string] : []
        ) => {
          const virtualSource = args[0];

          useVirtualBackgroundStore.setState(() => {
            if (type === 'none') {
              return {
                virtualBackground: {
                  virtualSource: undefined,
                  backgroundType: 'none',
                  blurValue: undefined,
                },
              };
            }
            if (type === 'half-blur') {
              return {
                virtualBackground: {
                  virtualSource: undefined,
                  backgroundType: 'blur',
                  blurValue: 8,
                },
              };
            }
            if (type === 'blur') {
              return {
                virtualBackground: {
                  virtualSource: undefined,
                  backgroundType: 'blur',
                  blurValue: 25,
                },
              };
            }
            return {
              virtualBackground: {
                virtualSource: virtualSource,
                backgroundType: 'image',
                blurValue: undefined,
              },
            };
          });
        },
        promotePreviewVirtualBackground: () => {
          set(state => ({
            virtualBackground: state.previewVirtualBackground,
          }));
        },
        selectBackground: (id: string) => {
          set(state => {
            const updatedBackgrounds = state.backgrounds.map(background => ({
              ...background,
              selected: background.id === id,
            }));
            return { backgrounds: updatedBackgrounds };
          });
        },
        reset: () =>
          set(state => ({
            ...initialState,
            virtualBackground: state.virtualBackground,
            previewVirtualBackground: state.previewVirtualBackground,
            selectedBackgroundId: state.selectedBackgroundId,
          })),
      })),
      {
        name: 'cometchat-vb-store',
        partialize: state => ({
          virtualBackground: state.virtualBackground,
          previewVirtualBackground: state.previewVirtualBackground,
          selectedBackgroundId: state.selectedBackgroundId,
        }),
      },
    ),
  ),
);

const setVirtualBackgroundEffect = (
  track: any,
  virtualBackground: IBackgroundEffectOptions['virtualBackground'],
) => {
  if (!track) {
    return;
  }
  if (virtualBackground.backgroundType === 'none') {
    track.setEffect(undefined);
  } else {
    createVirtualBackgroundEffect(virtualBackground).then(effect => {
      track.setEffect(effect);
    });
  }
};

export const setVirtualBackgroundBlurLevel = (blurValue = 8) => {
  useVirtualBackgroundStore.setState({
    virtualBackground: {
      virtualSource: undefined,
      backgroundType: 'blur',
      blurValue: blurValue,
    },
  });
};

export const setVirtualBackgroundImage = (imageUrl: string) => {
  useVirtualBackgroundStore.setState({
    virtualBackground: {
      virtualSource: imageUrl,
      backgroundType: 'image',
      blurValue: undefined,
    },
  });
};

useVirtualBackgroundStore.subscribe(
  state => state.virtualBackground,
  virtualBackground => {
    const localVideoTrack = getLocalTrack(MEDIA_TYPE.VIDEO)?.originalTrack;
    if (!localVideoTrack) {
      return;
    }
    if (virtualBackground.backgroundType === 'none') {
      localVideoTrack.setEffect(undefined);
    } else {
      setVirtualBackgroundEffect(localVideoTrack, virtualBackground);
    }
  },
);

useVirtualBackgroundStore.subscribe(
  state => state.previewVirtualBackground,
  previewVirtualBackground => {
    const previewTrack = useVirtualBackgroundStore.getState().previewTrack;
    setVirtualBackgroundEffect(previewTrack, previewVirtualBackground);
  },
);

useVirtualBackgroundStore.subscribe(
  state => state.previewTrack,
  (previewTrack, prevPreviewTrack) => {
    if (previewTrack) {
      const previewVirtualBackground =
        useVirtualBackgroundStore.getState().previewVirtualBackground;
      setVirtualBackgroundEffect(previewTrack, previewVirtualBackground);
    }
    if (prevPreviewTrack) {
      if (prevPreviewTrack.disposed !== true) {
        prevPreviewTrack.dispose();
      }
    }
  },
);

useVirtualBackgroundStore.subscribe(
  state => state.backgrounds,
  backgrounds => {
    const selectedBackground = backgrounds.find(
      (b: BackgroundOption) => b.selected,
    );
    if (selectedBackground) {
      const backgroundId = selectedBackground.id;
      const state = useVirtualBackgroundStore.getState();
      if (backgroundId === 'none') {
        state.setPreviewVirtualBackground('none');
      } else if (backgroundId === 'half-blur') {
        state.setPreviewVirtualBackground('half-blur');
      } else if (backgroundId === 'blur') {
        state.setPreviewVirtualBackground('blur');
      } else {
        const backgroundOption = backgrounds.find(
          (option: BackgroundOption) => option.id === backgroundId,
        );
        if (backgroundOption?.src) {
          state.setPreviewVirtualBackground('image', backgroundOption.src);
        }
      }
    }
  },
);

useTracksStore.subscribe(
  state =>
    state.tracks.find(t => t.mediaType === MEDIA_TYPE.VIDEO && t.local)
      ?.originalTrack,
  videoTrack => {
    if (videoTrack) {
      const virtualBackground =
        useVirtualBackgroundStore.getState().virtualBackground;
      setVirtualBackgroundEffect(videoTrack, virtualBackground);
    }
  },
);
